import { jsx, jsxs } from "react/jsx-runtime";
import { ArrowLeftIcon, CloseIcon } from "@dion-web/ui-icons";
import styled from "@mui/material/styles/styled";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import every from "lodash/every";
import isFunction from "lodash/isFunction";
import isUndefined from "lodash/isUndefined";
import React, { useEffect, useMemo, useState } from "react";
import { useCurrentColor } from "../../shared/hooks/current-color";
import { getMultilineEllipsisSX } from "../../shared/utils/styles";
import THEME, { DION_SCROLLBAR } from "../../theme";
import Box from "../Box";
import Button, { EButtonSize, EButtonVariants } from "../Button";
import Stack from "../Stack";
import Typography from "../Typography";
const {
  actionsTopMargin: ACTIONS_TOP_MARGIN,
  actionsBottomMargin: ACTIONS_BOTTOM_MARGIN,
  margin: DRAWER_MARGIN,
  subtitleTopMargin: SUBTITLE_TOP_MARGIN,
  titleBottomMargin: TITLE_BOTTOM_MARGIN
} = THEME.drawer.spacing;
const BORDER_RADIUS = THEME.drawer?.borderRadius?.top ?? THEME.radius.l;
const SCROLL_PADDING = THEME.scroll.spacing.sidePadding;
const SCROLL_WIDTH = THEME.scroll.size.m;
const BUTTON_SPACING = THEME.buttonGroup.spacing.m;
const DRAWER_MAX_HEIGHT = "90";
const MAX_CONTROLS = 3;
const TITLE_MAX_LINES = 3;
const SUBTITLE_MAX_LINES = 3;
const BACK_BUTTON_SX = {
  marginTop: "-6px",
  // TODO в токен
  marginLeft: "-8px"
  // TODO в токен
};
const CLOSE_BUTTON_SX = {
  marginTop: "-6px",
  // TODO в токен
  marginRight: "-10px"
  // TODO в токен
};
const SWAP_MARKER_SX = {
  width: 36,
  height: 5,
  background: "var(--mui-palette-elevationObject-lowAccent)",
  borderRadius: 4,
  position: "absolute",
  top: 4,
  left: "calc(50% - 18px)"
};
const getTitleSX = (hasBackClick = false, hasSubtitle = false) => {
  return {
    maxWidth: "100%",
    wordWrap: "break-word",
    paddingLeft: hasBackClick ? "16px" : "0px",
    // TODO в токен
    paddingRight: hasSubtitle ? "0px" : "16px",
    // TODO в токен
    ...getMultilineEllipsisSX(hasSubtitle ? SUBTITLE_MAX_LINES : TITLE_MAX_LINES),
    maxHeight: `${hasSubtitle ? SUBTITLE_MAX_LINES : TITLE_MAX_LINES}lh`
    // ?
  };
};
const TitleContainer = styled(Stack)(() => ({
  marginBottom: TITLE_BOTTOM_MARGIN,
  padding: `0 ${DRAWER_MARGIN}px`,
  justifyContent: "space-between"
}));
const ContentContainer = styled(Box)(({ fullWidth }) => ({
  wordWrap: "break-word",
  // overflow: 'auto',
  overflowY: "scroll",
  overflowX: "hidden",
  ...DION_SCROLLBAR,
  paddingRight: `${SCROLL_PADDING}px`,
  marginRight: fullWidth ? `-${DRAWER_MARGIN - SCROLL_WIDTH}px` : `${DRAWER_MARGIN - SCROLL_WIDTH - SCROLL_PADDING}px`,
  marginLeft: fullWidth ? "0" : `${DRAWER_MARGIN}px`,
  minHeight: "24px"
}));
const ControlsContainer = styled(Stack)(() => ({
  marginTop: ACTIONS_TOP_MARGIN,
  padding: `0 ${DRAWER_MARGIN}px`,
  alignItems: "center",
  justifyContent: "space-between"
}));
const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => {
  return {
    "& .MuiPaper-root": {
      maxHeight: `${DRAWER_MAX_HEIGHT}dvh`,
      paddingTop: `${DRAWER_MARGIN}px`,
      backgroundColor: theme.vars.palette.elevationObject.main,
      // borderTopLeftRadius: THEME.radius.m,
      // borderTopRightRadius: THEME.radius.m,
      paddingBottom: `${ACTIONS_BOTTOM_MARGIN}px`,
      borderTopLeftRadius: `${BORDER_RADIUS}px`,
      borderTopRightRadius: `${BORDER_RADIUS}px`,
      backgroundImage: "none",
      boxShadow: "none",
      overflowX: "hidden"
    }
  };
});
const Drawer = (props) => {
  const {
    title,
    subTitle,
    controls,
    open,
    onClose,
    onOpen,
    onBackClick,
    fixedHeight,
    fullWidth,
    className
  } = props;
  const [drawerRef, setDrawerRef] = useState();
  const allControls = React.useMemo(() => [controls].slice(0, MAX_CONTROLS), [controls]);
  const showControls = !every(allControls, isUndefined);
  const hasBackButton = isFunction(onBackClick);
  const [prevOpen, setPrevOpen] = React.useState(open);
  const {
    ctx,
    Provider
  } = useCurrentColor("elevationObject", "contrast", true);
  React.useEffect(() => {
    if (!prevOpen && open && isFunction(onOpen))
      onOpen();
    setPrevOpen(open);
  }, [open]);
  useEffect(() => {
    if (drawerRef)
      drawerRef.title = "";
  }, [drawerRef]);
  const paperProps = useMemo(() => ({
    sx: {
      height: `min(100dvh, ${fixedHeight}px)`,
      minHeight: `min(100dvh, ${fixedHeight}px)`
    }
  }), [fixedHeight]);
  const modalProps = useMemo(() => ({
    sx: {
      top: `min(100dvh, calc(100dvh  - ${fixedHeight}px))`
    }
  }), [fixedHeight]);
  const titleJustifyContent = !hasBackButton && !title ? "end" : "space-between";
  return /* @__PURE__ */ jsx(Provider, { value: ctx, children: /* @__PURE__ */ jsxs(
    StyledSwipeableDrawer,
    {
      ...props,
      open,
      anchor: "bottom",
      hideBackdrop: !!fixedHeight,
      ModalProps: fixedHeight ? modalProps : void 0,
      PaperProps: fixedHeight ? paperProps : void 0,
      ref: setDrawerRef,
      className,
      children: [
        /* @__PURE__ */ jsx(Box, { className: "swap-marker", sx: SWAP_MARKER_SX }),
        /* @__PURE__ */ jsxs(TitleContainer, { direction: "column", children: [
          /* @__PURE__ */ jsxs(Stack, { direction: "row", width: "100%", display: "flex", justifyContent: titleJustifyContent, children: [
            hasBackButton && /* @__PURE__ */ jsx(
              Button,
              {
                onClick: onBackClick,
                variant: EButtonVariants.GHOST,
                startIcon: /* @__PURE__ */ jsx(ArrowLeftIcon, {}),
                size: EButtonSize.M,
                deprecatedSX: BACK_BUTTON_SX
              }
            ),
            title && /* @__PURE__ */ jsx(Typography, { sx: getTitleSX(hasBackButton), flexGrow: 1, variant: "h4", children: title }),
            /* @__PURE__ */ jsx(
              Button,
              {
                onClick: onClose,
                variant: EButtonVariants.GHOST,
                startIcon: /* @__PURE__ */ jsx(CloseIcon, {}),
                size: EButtonSize.M,
                deprecatedSX: CLOSE_BUTTON_SX
              }
            )
          ] }),
          title && subTitle && /* @__PURE__ */ jsx(Box, { mt: `${SUBTITLE_TOP_MARGIN}px`, children: /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "body1",
              color: "lowContrast",
              sx: getTitleSX(false, true),
              children: subTitle
            }
          ) })
        ] }),
        props.children && /* @__PURE__ */ jsx(ContentContainer, { fullWidth, children: props.children }),
        showControls && /* @__PURE__ */ jsx(ControlsContainer, { direction: "column", spacing: `${BUTTON_SPACING}px`, children: React.Children.map(
          allControls,
          (child) => child && React.cloneElement(child, { fullWidth: true })
        ) })
      ]
    }
  ) });
};
var Drawer_default = Drawer;
export {
  Drawer_default as default
};
